
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sparkles, ArrowRight, ArrowLeft, Eye, Smile, Zap, Heart, Droplet, Menu } from 'lucide-react';

export type ConcernTag = 'BrightEyes' | 'NaturalLip' | 'YouthRefresh' | 'HairGlow' | 'Wellness' | 'FullMenu';

interface QuizQuestion {
  id: number;
  question: string;
  type: 'concerns' | 'budget' | 'timeCommitment' | 'resultsGoal';
  options: Array<{
    label: string;
    value: string;
    tag?: ConcernTag;
    icon?: React.ComponentType<any>;
  }>;
  multiple?: boolean;
}

interface QuizState {
  concerns: ConcernTag[];
  budget: string;
  timeCommitment: string;
  resultsGoal: string;
}

interface JUVTriageQuizProps {
  onComplete: (results: QuizState) => void;
}

const JUVTriageQuiz = ({ onComplete }: JUVTriageQuizProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizState>({
    concerns: [],
    budget: '',
    timeCommitment: '',
    resultsGoal: ''
  });

  const questions: QuizQuestion[] = [
    {
      id: 1,
      question: "What brings you in today? (Select all that apply)",
      type: 'concerns',
      multiple: true,
      options: [
        { 
          label: 'Eyes & Wrinkles', 
          value: 'eyes-wrinkles', 
          tag: 'BrightEyes',
          icon: Eye
        },
        { 
          label: 'Lips or Smile', 
          value: 'lips-smile', 
          tag: 'NaturalLip',
          icon: Smile
        },
        { 
          label: 'Skin Texture / Acne Scars', 
          value: 'skin-texture', 
          tag: 'YouthRefresh',
          icon: Zap
        },
        { 
          label: 'Hair Thinning / Hair Loss', 
          value: 'hair-loss', 
          tag: 'HairGlow',
          icon: Heart
        },
        { 
          label: 'Fatigue / Weight / Sleep', 
          value: 'wellness', 
          tag: 'Wellness',
          icon: Droplet
        },
        { 
          label: 'Not Sure / Show Me All', 
          value: 'show-all', 
          tag: 'FullMenu',
          icon: Menu
        }
      ]
    },
    {
      id: 2,
      question: "What's your budget range?",
      type: 'budget',
      options: [
        { label: 'Under $500', value: 'under-500' },
        { label: 'Under $1,000', value: 'under-1000' },
        { label: 'Open to bundle packages', value: 'open-bundle' },
        { label: 'Budget not a concern', value: 'no-budget-limit' }
      ]
    },
    {
      id: 3,
      question: "How much time can you commit?",
      type: 'timeCommitment',
      options: [
        { label: 'Quick visit (under 30 min)', value: 'quick' },
        { label: 'Standard session (60 min)', value: 'standard' },
        { label: 'Multi-session package', value: 'multi-session' },
        { label: 'Flexible timing', value: 'flexible' }
      ]
    },
    {
      id: 4,
      question: "What's your desired result?",
      type: 'resultsGoal',
      options: [
        { label: 'Subtle enhancement', value: 'subtle' },
        { label: 'Natural improvement', value: 'natural' },
        { label: 'Transformational change', value: 'transformational' },
        { label: 'Maintenance & prevention', value: 'maintenance' }
      ]
    }
  ];

  const handleAnswer = (questionType: string, value: string, tag?: ConcernTag) => {
    if (questionType === 'concerns') {
      if (tag) {
        const currentConcerns = answers.concerns;
        const isSelected = currentConcerns.includes(tag);
        
        setAnswers(prev => ({
          ...prev,
          concerns: isSelected 
            ? currentConcerns.filter(c => c !== tag)
            : [...currentConcerns, tag]
        }));
      }
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionType]: value
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(answers);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepComplete = () => {
    const currentQuestion = questions[currentStep];
    
    switch (currentQuestion.type) {
      case 'concerns':
        return answers.concerns.length > 0;
      case 'budget':
        return answers.budget !== '';
      case 'timeCommitment':
        return answers.timeCommitment !== '';
      case 'resultsGoal':
        return answers.resultsGoal !== '';
      default:
        return false;
    }
  };

  const progress = ((currentStep + 1) / questions.length) * 100;
  const currentQuestion = questions[currentStep];

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {questions.length}
          </span>
          <span className="text-sm text-juv-terracotta font-medium">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-juv-deep-chestnut">
                {currentQuestion.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = currentQuestion.type === 'concerns' 
                  ? option.tag && answers.concerns.includes(option.tag)
                  : answers[currentQuestion.type as keyof QuizState] === option.value;
                
                const Icon = option.icon;

                return (
                  <Button
                    key={index}
                    variant={isSelected ? "default" : "outline"}
                    className={`w-full text-left justify-start p-4 h-auto ${
                      isSelected 
                        ? 'bg-juv-terracotta hover:bg-juv-deep-chestnut text-white' 
                        : 'hover:bg-juv-soft-blush border-juv-rose-clay/50'
                    }`}
                    onClick={() => handleAnswer(currentQuestion.type, option.value, option.tag)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
                      <span className="flex-1">{option.label}</span>
                    </div>
                  </Button>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 0}
          className="flex items-center gap-2 border-juv-rose-clay text-juv-deep-chestnut"
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </Button>
        <Button
          onClick={nextStep}
          disabled={!isStepComplete()}
          className="flex items-center gap-2 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
        >
          {currentStep === questions.length - 1 ? 'See Results' : 'Next'}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default JUVTriageQuiz;
